import Vue from 'vue'
import confetti from 'canvas-confetti'
import _filterProfaneWords from 'storybook-utils/profanity-filter/filterProfaneWords'
import Sentry from '@/3rd-parties/sentry'
import xss from 'xss'
import anchorme from 'anchorme'
import storage from '@/services/storage'
import Constant from '@/constant'
import Config from '@/config'
import shuffleArrayWithSeed from '@/components/utils/shuffleArrayWithSeed'
import roundNumberWithNDecimals from '@/components/utils/roundNumberWithNDecimals'
import cookie from '@/services/cookie'
import store from './store'
import mixpanelUtils from './utils/mixpanel.utils'
import syncTime from './utils/syncTime.utils'
import {
  isCustomFontToken,
  applyCustomFont,
  customFontStack,
} from './utils/customFont'

function ga4() {
  if (Config.region === Constant.euRegion) {
    return
  }
  try {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  } catch (e) {
    Sentry.captureException(e)
  }
}

const getRandomEmoji = () => {
  const numberOfEmojis = Constant.emojis.length
  const randomNumber = Math.floor(Math.random() * numberOfEmojis)

  return Constant.emojis[randomNumber]
}

export default {
  // sample url: http://localhost:8081/a01798ffpr#2 (1-indexed)
  // return a @{number} (0-indexed) from url as currentSlideIndex
  getCurrentSlideIndexFromUrl: () => {
    if (!window.location.hash) return 0
    const id = window.location.hash.replace('#', '')
    return id <= 0 ? 0 : Number(id) - 1
  },

  // The query string carries marketplaceSandboxId, which resolveMarketplaceApiUrl
  // re-reads on every call; rebuilding the slide URL from pathname alone drops it
  // and the marketplace silently falls back to staging.
  buildSlideUrl: (oneIndexedSlideNumber, location = window.location) => {
    return `${location.pathname}${location.search}#${oneIndexedSlideNumber}`
  },

  filterProfaneWords: (text) => {
    return store.state.presentation &&
      store.state.presentation.filteringProfanity
      ? _filterProfaneWords(text)
      : text
  },
  // function tracking to mixpanel and ga4
  getRandomEmoji,
  trackGA4AndMixpanel: (
    eventCategory = '',
    eventAction = '',
    eventLabel = '',
    eventValue,
    customProps = {},
  ) => {
    this.mixpanelTrack(eventCategory, eventAction, eventLabel, eventValue, customProps)
  },
  mixpanelTrack: (
    eventCategory,
    eventAction,
    eventLabel,
    eventValue,
    customProps = {},
  ) => {
    mixpanelUtils.track(eventAction, {
      event_category: eventCategory,
      event_label: eventLabel,
      event_value: eventValue,
      ...customProps,
    })
  },

  ga4Set: (property, payload) => {
    ga4('set', property, payload)
  },

  ga4Track: (eventName, payload) => {
    const data = {
      eventCategory: 'Audience',
      eventAction: eventName,
      ...payload,
    }

    if (Constant.stoppedSendingToGoogleTagEvents.includes(eventName)) {
      // include send_to: 'GTM-PMZC62P' to stop sending to Google Tag Manager
      data.send_to = Config.gtmId
    }

    ga4('event', eventName, data)
  },

  gaTrackPageView: (url, title) => {
    mixpanelUtils.track('Page View', {
      title,
    })
  },

  getParamValue: (param, url) => {
    if (!url) url = window.location.href
    param = param.replace(/[\\[\]]/g, '\\$&')
    const regex = new RegExp(`[?&]${param}(=([^&#]*)|&|#|$)`)
    const results = regex.exec(url)
    if (!results) return null
    if (!results[2]) return ''
    return decodeURIComponent(results[2].replace(/\+/g, ' '))
  },

  /**
   * @deprecated
   * Using slide.util.js instead
   */
  formatSlide: (slide) => {
    if (!slide.SlideOptions) {
      slide.SlideOptions = []
    }
    slide.SlideOptions.sort((a, b) => {
      return a.order - b.order
    })
    if (slide.hints && slide.hints.length) {
      slide.hints.sort((a, b) => {
        return a.order - b.order
      })
    }
    for (let i = 0; i < slide.SlideOptions.length; i++) {
      if (
        slide.SlideOptions[i].audience &&
        slide.SlideOptions[i].audience === store.state.audienceId
      ) {
        slide.openEndedSubmited = true
        break
      }
    }

    if (slide.answer && slide.answer.vote.length) {
      [slide.radioSelectded] = slide.answer.vote
    }

    if (typeof slide.otherCorrectQuiz === 'string') {
      try {
        slide.otherCorrectQuiz = JSON.parse(slide.otherCorrectQuiz)
      } catch (error) {
        slide.otherCorrectQuiz = []
      }
    }

    if (!slide.otherCorrectQuiz) {
      slide.otherCorrectQuiz = []
    }

    return slide
  },

  isURL(str) {
    const pattern = /^(https?:\/\/)?([\w.-]+)(\.[a-z]{2,6})(:[\d]+)?(\/[\w .-]*)*\/?(\?[=&\w.%-]*)*$/
    return pattern.test(str)
  },

  timeToString(seconds) {
    // multiply by 1000 because Date() requires miliseconds
    const date = new Date(seconds * 1000)
    let mm = date.getUTCMinutes()
    let ss = date.getSeconds()
    // If you were building a timestamp instead of a duration, you would uncomment the following line to get 12-hour (not 24) time
    // if (hh > 12) {hh = hh % 12;}
    // These lines ensure you have two-digits
    if (mm < 10) {
      mm = `0${mm}`
    }
    if (ss < 10) {
      ss = `0${ss}`
    }
    // This formats your string to HH:MM:SS
    return `${mm}:${ss}`
  },

  checkCorrectAnswer(vote, options) {
    const corrects = []
    let count = 0
    options.forEach((item) => {
      if (item.correct) {
        corrects.push(item.id)
      }
    })
    if (vote.length != corrects.length) {
      return false
    }
    vote.forEach((item) => {
      if (corrects.indexOf(item) !== -1) {
        count++
      }
    })

    if (count && count == corrects.length) {
      return true
    }
    return false
  },

  enableAudienceAdmissionForm() {
    const {
      audienceAdmission,
      disableFormJoinPresentation,
    } = store.state.presentation
    let enableAudienceAdmissionForm = false
    if (
      !disableFormJoinPresentation &&
      audienceAdmission &&
      audienceAdmission.isAudienceAdmission &&
      audienceAdmission.fields &&
      audienceAdmission.fields.length
    ) {
      audienceAdmission.fields.forEach((item) => {
        if (item.type === 'name' && !store.state.audienceName) {
          enableAudienceAdmissionForm = true
        }
        if (item.type === 'email' && !store.state.audienceEmail) {
          enableAudienceAdmissionForm = true
        }
        if (item.type === 'team' && !store.state.audienceTeam) {
          enableAudienceAdmissionForm = true
        }
      })
    }
    return enableAudienceAdmissionForm
  },

  getTimeCount(seconds, self) {
    const textSeconds = self.$t('seconds')
    const textMinutes = self.$t('minutes')
    const textMinute = self.$t('minute')

    const minutes = Math.floor(seconds / 60)
    const secondsWithinMinute = seconds % 60
    let result = ''

    if (minutes) {
      if (minutes === 1) {
        result += `${minutes} ${textMinute} `
      } else {
        result += `${minutes} ${textMinutes} `
      }
    }

    result += `${secondsWithinMinute.toFixed(2)} ${textSeconds}`

    return result
  },
  setFontSlideV2(font) {
    if (isCustomFontToken(font)) {
      const customStack = customFontStack(font)
      const canvas = document.getElementById('middle-canvas')
      if (canvas) {
        canvas.style.fontFamily = customStack
      }
      document.body.style.fontFamily = customStack
      try {
        applyCustomFont(font)
      } catch (error) {
        // fallback stack is already applied
      }
      return
    }
    // Inline styles replace the global stack, so keep Cairo behind the chosen font for Arabic glyphs
    const fontStack = `"${font}", Cairo, sans-serif`
    const el = document.getElementById('middle-canvas')
    if (el) {
      el.style.fontFamily = fontStack
    }
    // find font size exist in head
    const { head } = document
    const fonts = head.querySelectorAll('link[rel="stylesheet"]')
    let isExist = false
    fonts.forEach((item) => {
      if (item.href.includes(font) || item.href.includes(font.replace(/ /g, '+'))) {
        isExist = true
      }
    })
    if (!isExist) {
      const link = `https://fonts.googleapis.com/css?family=${font}`
      const el = document.createElement('link')
      el.href = link
      el.rel = 'stylesheet'
      document.head.appendChild(el)
    }
    document.body.style.fontFamily = fontStack
  },

  setFontSlide(font) {
    const data = Constant.fonts[font]
    const headID = document.getElementsByTagName('head')[0]
    const link = document.createElement('link')
    link.type = 'text/css'
    link.rel = 'stylesheet'

    link.href = data.url
    headID.appendChild(link)
    const newStyle = document.createElement('style')
    newStyle.appendChild(
      document.createTextNode(
        `\
    @font-face {\
        font-family: ${data.value};\
        src: url('${data.url}');\
    }\
    `,
      ),
    )

    document.head.appendChild(newStyle)
    document.body.style.fontFamily = data.value
  },
  getColorByStr(str) {
    const colorPalette = store.getters['presentationAttributeModule/presentationColorPalette']
    const numberOfColors = colorPalette.length
    const number = str.charCodeAt(0)

    return colorPalette[number % numberOfColors]
  },
  updateDataQuestionAnswer(arr, tabQnaQuestion) {
    if (tabQnaQuestion === 1) {
      arr.sort((a, b) => {
        return new Date(a.createdAt) - new Date(b.createdAt)
      })
    }

    if (tabQnaQuestion === 2) {
      arr.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt)
      })
    }

    if (tabQnaQuestion === 1) {
      arr.sort((a, b) => {
        return b.voteCount - a.voteCount
      })
    }

    if (tabQnaQuestion === 1) {
      arr.sort((a, b) => {
        return b.pinned - a.pinned
      })
    }

    if (tabQnaQuestion === 3) {
      arr.sort((a, b) => {
        return b.answeredTimestamp - a.answeredTimestamp
      })
    }

    arr.forEach((question) => {
      if (question.answered) question.status = 'approved'
      if (question.vote.includes(store.state.audienceId)) {
        question.voted = true
      }
    })

    this._updateQnAQuestionCount(arr)

    store.state.questions = arr
  },

  _updateQnAQuestionCount(questions) {
    const showOtherQuestions = store.state.presentation.qnaAudienceShowAll
    let questionCount = 0
    let questionFilterByShowAll = []
    if (!showOtherQuestions) {
      questionFilterByShowAll = questions.filter((question) => {
        return question.audience === store.state.audienceId
      })
    } else {
      questionFilterByShowAll = questions
    }

    if (store.state.presentation.isModerationMode) {
      questionCount = questionFilterByShowAll.filter((question) => {
        return question.status === 'approved'
      }).length
    } else {
      questionCount = questionFilterByShowAll.filter((question) => {
        return question.status !== 'dismissed'
      }).length
    }
    store.state.questionCount = questionCount
  },

  numberWithCommas(number) {
    return number.toLocaleString(store.state.presentation.language)
  },

  hexToRgba(hex, opacity = 1) {
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      let c = hex.substring(1).split('')
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]]
      }
      c = `0x${c.join('')}`
      return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(
        ',',
      )}, ${opacity})`
    }

    return null
  },

  changeRgbAlpha: (color, opacity) => {
    if (!color) return color
    // eslint-disable-next-line no-useless-escape
    const regex = /[\d\.]+\)$/g
    if (opacity) {
      color = color.replace(regex, opacity)
    }
    return color
  },

  validateEmail: (email) => {
    // eslint-disable-next-line no-useless-escape
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email).toLowerCase())
  },
  getAhaColor(number) {
    const colorPalette = store.getters['presentationAttributeModule/presentationColorPalette']
    const index = number % colorPalette.length
    return colorPalette[index]
  },

  checkWhiteLabelDomain() {
    if (window.location.hostname === 'localhost') return false
    return window.location.hostname.indexOf(Config.domain) === -1
  },
  animateClass({ animation, isInfinity, duration }) {
    if (!animation) return null
    return `animate__animated animate__${animation} ${isInfinity ? 'animate__infinite' : ''} ${duration ? `animate__delay-${duration}s` : ''} `
  },

  showToastSuccess(text, className, action) {
    if (!text) return
    Vue.toasted.success(text, {
      theme: 'bubble',
      position: 'bottom-right',
      className,
      action: action
        ? {
          text: '',
          onClick: (e, toastObject) => {
            toastObject.goAway(0)
          },
        }
        : '',
    })
  },

  showToastInfo(text, className, action) {
    if (!text) return
    Vue.toasted.info(text, {
      theme: 'bubble',
      position: 'bottom-right',
      className,
      action: action
        ? {
          text: '',
          onClick: (e, toastObject) => {
            toastObject.goAway(0)
          },
        }
        : '',
    })
  },

  showToastError(text, className, action) {
    if (!text) return
    Vue.toasted.error(text, {
      theme: 'bubble',
      position: 'bottom-right',
      className,
      action: action
        ? {
          text: '',
          onClick: (e, toastObject) => {
            toastObject.goAway(0)
          },
        }
        : '',
    })
  },

  // Surface a failed answer submission (a network/5xx failure that survived the
  // retries, or a 411 closed submission) to the audience: shows an error toast
  // and fires the GA4 failure event. Returns whether the host has closed
  // submissions (retrying can't help) so the caller can decide whether to
  // re-enable its submit control. `t` is the caller's i18n translate function.
  reportAnswerSubmitFailure(res, {
    t, toastClassName, presentationId, slideId, slideType,
  }) {
    const isSubmissionClosed = !!(res && (res.status === 411 || res.errorCode === 411))
    const errorText = isSubmissionClosed ? t('submissionClosedButton') : t('openendedSubmissionFail')
    this.showToastError(errorText, toastClassName)
    this.ga4Track('Audience Submit Answer Failed', {
      presentation_id: presentationId,
      slide_id: slideId,
      slide_type: slideType,
      error_code: (res && (res.errorCode || res.status)) || undefined,
    })
    return { isSubmissionClosed, errorText }
  },

  setupConfettiEffect(canvasElement, canvasOptions, effectOptions) {
    const effectCreator = confetti.create(canvasElement, canvasOptions)
    return (origin) => {
      effectCreator({
        ...effectOptions,
        origin,
      })
    }
  },

  isIosSafary() {
    try {
      // FIXME: Consider removing this as there should be any
      // reason why we need a specific class for safari.
      // This was added by Loi to fix an issue with safari
      // but we lost track of the original issue. @huongnguyen2152 15.05.2020
      // eslint-disable-next-line
      let isSafari =
        /constructor/i.test(window.HTMLElement) ||
        (function (p) {
          return p.toString() === '[object SafariRemoteNotification]'
        }(
          !window.safari ||
            (typeof window.safari !== 'undefined' && window.safari.pushNotification),
        ))
      const ua = window.navigator.userAgent
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
      const webkit = !!ua.match(/WebKit/i)
      const iOSSafari = iOS && webkit && !ua.match(/CriOS/i)
      if (isSafari || iOSSafari) {
        return true
      }
      return false
    } catch (error) {
      Sentry.captureException(error)
      return false
    }
  },
  formatNumber(number) {
    if (!number) return 0
    if (!store.state.presentation.language) return number
    if (typeof number === 'string') number = number.replace(',', '')
    // AHA-14204: parseFloat can't understand ',' character, so: 2,991 => 2 can cause wrong error.
    // Data type of number sometime is 'string', sometime is 'number'
    return parseFloat(number).toLocaleString(store.state.presentation.language)
  },

  roundNumberWithNDecimals,

  extractContent(s, space) {
    const div = document.createElement('div')
    div.innerHTML = s
    if (/img|video|audio/g.test(s)) return false
    if (space) {
      const children = div.querySelectorAll('*')
      for (let i = 0; i < children.length; i++) {
        if (children[i].textContent) {
          children[i].textContent += ' '
        } else {
          children[i].innerText += ' '
        }
      }
    }
    return [div.textContent || div.innerText]
      .toString()
      .trim()
      .replace(/[\n\t\r]/g, '')
      .replace(/ +/g, ' ')
  },

  toEnglishRankingSuffix(number) {
    const j = number % 10
    const k = number % 100

    if (j === 1 && k !== 11) return `${number}st`
    if (j === 2 && k !== 12) return `${number}nd`
    if (j === 3 && k !== 13) return `${number}rd`

    return `${number}th`
  },

  countNumberOfCorrectAnswers(slideOptions) {
    const allCorrectAnswers = slideOptions.filter((option) => {
      return option.correct === true
    })
    if (!allCorrectAnswers.length) return 0
    return allCorrectAnswers.length
  },
  getDataCorrectAnswers(slideOptions, result) {
    const totalCorrectAnswers = this.countNumberOfCorrectAnswers(slideOptions)
    const allCorrectAnswersIds = []
    slideOptions.forEach((option) => {
      if (option.correct === true) {
        allCorrectAnswersIds.push(option.id)
      }
    })
    const audienceCorrectAnswers = []
    if (result.vote) {
      result.vote.forEach((vote) => {
        if (allCorrectAnswersIds.includes(vote)) {
          audienceCorrectAnswers.push(vote)
        }
      })
    }
    const audienceMissCorrectAnswers =
      totalCorrectAnswers - audienceCorrectAnswers.length
    return {
      audienceCorrectAnswers,
      audienceMissCorrectAnswers,
      totalCorrectAnswers,
    }
  },
  getSlideType(slideActive) {
    // Because slide type is super confusing (we have 'slideType' & 'type' fields),
    // we need to clarify it here
    if (!slideActive) return ''
    let theTrueSlideType = slideActive.type

    // For quiz-related slide types
    if (slideActive.type === 'pickAnswer') {
      if (slideActive.slideType === 'imageChoice') {
        theTrueSlideType = 'pickImage'
      } else if (slideActive.slideType === 'typeAnswer') {
        theTrueSlideType = 'typeAnswer'
      }
    }

    // Other slide types remains ok
    return theTrueSlideType
  },
  getAudienceReferralSlug(presentation) {
    return (presentation
      && presentation.isEnableAudienceReferral
      && presentation.user
      && presentation.user.refby) || ''
  },
  generateButtonCTAURL(presentationID, slideActive, campaign) {
    const baseUrl = `${
      Config.presenterUrl
    }/pages/register?source=presentation_${presentationID}&medium=audience_app&campaign=${campaign}&content=${this.getSlideType(
      slideActive,
    )}`
    const { presentation } = store.state
    const refby = this.getAudienceReferralSlug(presentation)

    return refby ? `${baseUrl}&refby=${encodeURIComponent(refby)}` : baseUrl
  },
  trackGAButtonCTA(presentationID, slideActive) {
    this.mixpanelTrack(
      'Audience',
      'Last slide > Button CTA > Opens CTA URL',
      `Presentation ID: ${presentationID}, Slide type: ${this.getSlideType(
        slideActive,
      )}`,
    )
  },

  formatFields(audienceAdmissionFieldsFromSocket, presentationId = null) {
    if (!audienceAdmissionFieldsFromSocket) return []
    const fields = [...audienceAdmissionFieldsFromSocket]
    fields.forEach((item) => {
      if (!item.data) item.data = ''
      item.disabled = false
      if (item.type === 'name') {
        if (store.state.audienceName) {
          item.data = store.state.audienceName
        }
        if (!store.state.audienceName && store.state.currentUser && store.state.currentUser.id) {
          item.data = store.state.currentUser.firstName
        }
      }
      if (item.type === 'email') {
        if (store.state.audienceEmail) {
          item.data = store.state.audienceEmail
        }
        if (!store.state.audienceEmail && store.state.currentUser && store.state.currentUser.id) {
          item.data = store.state.currentUser.email
          // When the user arrived via the participant-verification gate they're already
          // logged in — lock the email field so it can't diverge from their account.
          item.disabled = true
        }
      }
      if (item.type === 'team' && store.state.audienceTeam) {
        item.data = store.state.audienceTeam
      }

      if (!Constant.systemField.includes(item.type)) {
        item.data = storage.getValueByPresentation(presentationId, `customField${item.type}`, '')
      }
    })
    return fields
  },

  shuffleArrayWithSeed,

  shuffleArray(array) {
    let currentIndex = array.length
    let randomIndex

    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex)
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ]
    }

    return array
  },
  fromMapToArray(map, keyName) {
    if (!map) return null
    return Object.keys(map).map((key) => ({ [keyName]: key, ...map[key] }))
  },
  toBinary(string) {
    const codeUnits = new Uint16Array(string.length)
    for (let i = 0; i < codeUnits.length; i++) {
      codeUnits[i] = string.charCodeAt(i)
    }
    return window.btoa(
      String.fromCharCode(...new Uint8Array(codeUnits.buffer)),
    )
  },
  getCorrectSlideTypeName(slide) {
    const { type, slideType } = slide
    const name = {
      spinnerWheel: 'spinnerWheel',
      leaderboard: 'leaderboard',
      imageChoice: 'poll',
      multipleChoice: 'poll',
      openEnded: 'openEnded',
      wordCloud: 'wordCloud',
      scale: 'scales',
      question: 'q&a',
      ideas: 'brainstorm',
      freestyle: 'content',
      textSlide: 'heading',
      bulletPoints: 'list',
      qrcode: 'qrCode',
      image: 'image',
      youtube: 'youtube',
      document: 'document',
      googleSlides: 'googleSlides',
    }
    if (type === 'pickAnswer') {
      if (slideType === null) return 'pickAnswer'
      if (slideType === 'imageChoice') return 'pickImage'
      if (slideType === 'typeAnswer') return 'typeAnswer'
      if (slideType === 'matchPairs') return 'matchPairs'
      if (slideType === 'correctOrder') return 'correctOrder'
    } else if (name[type]) {
      return name[type]
    }
    return 0
  },
  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop().split(';').shift()
    }
    return null
  },
  sanitize(text, isAllowedUnformattingTags, makeLinksClickable = true) {
    if (!text) return text

    const options = {
      whiteList: {
        i: [],
        u: [],
        b: [],
        em: [],
        br: [],
        ul: [],
        ol: [],
        li: [],
        h1: [],
        h2: [],
        h3: [],
        h4: [],
        sup: [],
        sub: [],
        strong: [],
        p: ['class'],
      },
    }

    if (isAllowedUnformattingTags) {
      options.whiteList = {
        ...options.whiteList,
        a: ['href', 'class', 'target'],
        source: ['src', 'type'],
        iframe: ['src', 'width', 'height', 'frameborder'],
        video: ['width', 'height', 'controls', 'autoplay'],
        audio: ['width', 'height', 'controls', 'autoplay'],
      }
    }

    const sanitizedText = xss(text, options)

    if (!store.state.presentation.showHyperLink || !isAllowedUnformattingTags) {
      return sanitizedText
    }
    if (makeLinksClickable) {
      return anchorme({
        input: sanitizedText,
        options: {
          attributes: {
            target: 'blank',
            class: 'text-hyper-link',
          },
        },
      })
    }
    return sanitizedText
  },
  hasLoggedInCookie() {
    return Boolean(cookie.getValue('ahaLoggedIn'))
  },
  isLoggedIn() {
    return this.hasLoggedInCookie() || Boolean(cookie.getValue('ahaToken'))
  },
  isEuOrWhiteLabel() {
    return Config.domain === 'ahaslides.eu' || this.checkWhiteLabelDomain()
  },
  ...syncTime,
}
